import React, { FormEvent, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AppButton from '../../../components/designe/app-button/AppButton';
import AppTextField from '../../../components/designe/app-text-field/AppTextField';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import { useAccess } from '../../../hooks/useAccess';
import {
  useMigrateOwnerStoragePaths,
  useMigratePublicStoragePaths,
  useVerifyFirebaseUserEmail,
} from '../../../services/redux/api/appConfigApi';
import {
  errorToastMessage,
  successToastMessage,
} from '../../../components/layout/toast-root/toast';
import { Messages } from '../../../shared/messages';
import { IMigrateStoragePathsResponse } from '../../../types/interfaces/migrate-storage-paths-response';

function MigrationResultPanel({
  result,
}: {
  result: IMigrateStoragePathsResponse;
}) {
  const { t } = useTranslation();
  const hasErrors = (result.errors?.length ?? 0) > 0;

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {hasErrors && (
        <Typography color="warning.main" variant="body2">
          {t('superAdminDashboard.migrateStoragePathsPartialFailure')}
        </Typography>
      )}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('superAdminDashboard.migrateStoragePathsMigratedFiles')}
        </Typography>
        <Typography variant="body1">{result.migratedFiles}</Typography>
      </Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('superAdminDashboard.migrateStoragePathsSkippedFiles')}
        </Typography>
        <Typography variant="body1">{result.skippedFiles}</Typography>
      </Box>
      {hasErrors && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('superAdminDashboard.migrateStoragePathsErrors')}
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {result.errors.map((err, i) => (
              <Box
                key={`${err.appFileId}-${i}`}
                sx={{
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}>
                <Typography variant="body2" component="div">
                  <strong>appFileId:</strong> {err.appFileId}
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                  {err.message}
                </Typography>
                {err.storageLink != null && err.storageLink !== '' && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                    {err.storageLink}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

function Other() {
  const { t } = useTranslation();
  const { access } = useAccess();
  const [email, setEmail] = useState('');
  const [verifyFirebaseUserEmail, { isLoading: verifyLoading }] =
    useVerifyFirebaseUserEmail();
  const [migratePublicStoragePaths, { isLoading: migratePublicLoading }] =
    useMigratePublicStoragePaths();
  const [migrateOwnerStoragePaths, { isLoading: migrateOwnerLoading }] =
    useMigrateOwnerStoragePaths();

  const [publicDialogOpen, setPublicDialogOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [publicMigrationResult, setPublicMigrationResult] =
    useState<IMigrateStoragePathsResponse | null>(null);
  const [ownerMigrationResult, setOwnerMigrationResult] =
    useState<IMigrateStoragePathsResponse | null>(null);

  const canVerifyFirebaseUserEmail =
    access?.superAdmin?.canVerifyFirebaseUserEmail === true;
  const canMigratePublicStoragePaths =
    access?.superAdmin?.canMigratePublicStoragePaths === true;
  const canMigrateOwnerStoragePaths =
    access?.superAdmin?.canMigrateOwnerStoragePaths === true;

  const showMigrationOrderHint =
    canMigratePublicStoragePaths && canMigrateOwnerStoragePaths;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      return;
    }

    try {
      const newlyVerified = await verifyFirebaseUserEmail({
        email: trimmed,
      }).unwrap();

      successToastMessage(
        newlyVerified
          ? t('superAdminDashboard.firebaseEmailVerifiedOk')
          : t('superAdminDashboard.firebaseEmailAlreadyVerified'),
      );
    } catch (err: any) {
      if (err?.status === 404 && err?.data?.code === 'USER_NOT_FOUND') {
        errorToastMessage(t('superAdminDashboard.firebaseEmailUserNotFound'));
        return;
      }

      const message =
        err?.data?.message || err?.message || `${Messages.someWrong}`;
      errorToastMessage(message);
    }
  }

  function onConfirmPublicMigrate() {
    setPublicDialogOpen(false);
    setPublicMigrationResult(null);

    migratePublicStoragePaths()
      .unwrap()
      .then((data) => {
        setPublicMigrationResult(data);
        successToastMessage(
          t('superAdminDashboard.migratePublicStoragePathsDone'),
        );
      })
      .catch((err: any) => {
        const message =
          err?.data?.message || err?.message || `${Messages.someWrong}`;
        errorToastMessage(message);
      });
  }

  function onConfirmOwnerMigrate() {
    setOwnerDialogOpen(false);
    setOwnerMigrationResult(null);

    migrateOwnerStoragePaths()
      .unwrap()
      .then((data) => {
        setOwnerMigrationResult(data);
        successToastMessage(
          t('superAdminDashboard.migrateOwnerStoragePathsDone'),
        );
      })
      .catch((err: any) => {
        const message =
          err?.data?.message || err?.message || `${Messages.someWrong}`;
        errorToastMessage(message);
      });
  }

  if (
    !canVerifyFirebaseUserEmail &&
    !canMigratePublicStoragePaths &&
    !canMigrateOwnerStoragePaths
  ) {
    return null;
  }

  return (
    <Stack spacing={4}>
      {canVerifyFirebaseUserEmail && (
        <Box component="form" onSubmit={onSubmit} maxWidth={480}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('superAdminDashboard.verifyFirebaseEmailTitle')}
          </Typography>
          <Stack
            spacing={2}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'flex-end' }}>
            <AppTextField
              fullWidth
              type="email"
              autoComplete="email"
              label={t('superAdminDashboard.verifyFirebaseEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={verifyLoading}
            />
            <AppButton
              type="submit"
              variant="contained"
              color="primary"
              loader={verifyLoading}
              sx={{ flexShrink: 0 }}>
              {t('superAdminDashboard.verifyFirebaseEmailSubmit')}
            </AppButton>
          </Stack>
        </Box>
      )}

      {showMigrationOrderHint && (
        <Typography variant="body2" color="text.secondary" maxWidth={720}>
          {t('superAdminDashboard.migrationOrderHint')}
        </Typography>
      )}

      {canMigratePublicStoragePaths && (
        <Box maxWidth={720}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('superAdminDashboard.migratePublicStoragePathsTitle')}
          </Typography>
          <AppButton
            variant="contained"
            color="primary"
            loader={migratePublicLoading}
            disabled={migratePublicLoading}
            onClick={() => setPublicDialogOpen(true)}>
            {t('superAdminDashboard.migratePublicStoragePathsButton')}
          </AppButton>

          {publicMigrationResult && (
            <MigrationResultPanel result={publicMigrationResult} />
          )}

          <ConfirmDialog
            open={publicDialogOpen}
            onClose={() => setPublicDialogOpen(false)}
            onSubmit={onConfirmPublicMigrate}
            title={t(
              'superAdminDashboard.migratePublicStoragePathsConfirmTitle',
            )}
            confirmTitleKey="common.confirm">
            {t('superAdminDashboard.migratePublicStoragePathsConfirmMessage')}
          </ConfirmDialog>
        </Box>
      )}

      {canMigrateOwnerStoragePaths && (
        <Box maxWidth={720}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('superAdminDashboard.migrateOwnerStoragePathsTitle')}
          </Typography>
          <AppButton
            variant="contained"
            color="primary"
            loader={migrateOwnerLoading}
            disabled={migrateOwnerLoading}
            onClick={() => setOwnerDialogOpen(true)}>
            {t('superAdminDashboard.migrateOwnerStoragePathsButton')}
          </AppButton>

          {ownerMigrationResult && (
            <MigrationResultPanel result={ownerMigrationResult} />
          )}

          <ConfirmDialog
            open={ownerDialogOpen}
            onClose={() => setOwnerDialogOpen(false)}
            onSubmit={onConfirmOwnerMigrate}
            title={t(
              'superAdminDashboard.migrateOwnerStoragePathsConfirmTitle',
            )}
            confirmTitleKey="common.confirm">
            {t('superAdminDashboard.migrateOwnerStoragePathsConfirmMessage')}
          </ConfirmDialog>
        </Box>
      )}
    </Stack>
  );
}

export default Other;
